import CmtCard from '@coremat/CmtCard';
import CmtCardContent from '@coremat/CmtCard/CmtCardContent';
import { Box, Button, Divider, Fab, Typography } from '@mui/material';
import React, { useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import makeStyles from '@mui/styles/makeStyles';
import { useTranslation } from 'react-i18next';
import { shouldShowQuantityPerPackageInfo } from 'services/Utilities/Utilities';

const useStyles = makeStyles(() => ({
  itemCardContent: () => ({
    padding: 0,
    position: 'relative',
  }),
  itemCard: () => ({
    borderRadius: 0,
  }),
  promoBadge: {
    position: 'absolute',
    top: 0,
    right: 0,
    backgroundColor: 'rgba(255, 221, 0, 0.88)',
    color: 'black',
    padding: '6px 24px',
    fontWeight: 'bold',
    fontSize: 18,
    zIndex: 1,
  },
  quickShopWrapper: {
    position: 'relative',
    '&:hover $quickShopOverlay, &:focus-within $quickShopOverlay': {
      opacity: 1,
      pointerEvents: 'auto',
    },
  },

  quickShopOverlay: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    backgroundColor: 'rgba(8, 8, 8, 0.45)',
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'center',

    opacity: 0,
    pointerEvents: 'none',
    transition: 'opacity 0.3s ease',

    padding: '0 16px 32px',
  },
}));

const ItemListView = ({
  title,
  identification,
  description,
  imgSrc,
  price,
  promotionPrice,
  quantityPerPackage,
  currency,
  actions,
  view,
  onClick,
  promotionQuantity,
  inventoriedQuantityAvailable,
  showStartAt,
  enableQuickShop = true,
  onQuickShop,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const isCard = useMemo(() => view === 'card', [view]);
  const showQuickShop = useMemo(() => {
    if (!enableQuickShop || !onQuickShop) {
      return false;
    }
    return actions?.some(action => action?.text === t('setUp'));
  }, [actions, enableQuickShop, onQuickShop, t]);

  const formattedDescription = useMemo(() => {
    return description?.split('\n').join('<br />');
  }, [description]);
  return (
    <CmtCard sx={{ height: '100%', padding: 0 }} className={classes.itemCard}>
      <CmtCardContent className={classes.itemCardContent} sx={{ height: '100%', padding: 0 }}>
        {promotionPrice > 0 && (
          <Box className={classes.promoBadge}>
            <Typography variant="h4">{t('onPromotion')}</Typography>
          </Box>
        )}

        <Box
          flexDirection={isCard ? 'column' : 'row'}
          display="flex"
          justifyContent={'space-between'}
          width={'100%'}
          height={'100%'}>
          <Box minWidth={'250px'} onClick={onClick} display="flex" flexDirection={isCard ? 'column' : 'row'}>
            <Box
              textAlign={'center'}
              className={showQuickShop ? classes.quickShopWrapper : undefined}
              tabIndex={showQuickShop ? 0 : undefined}>
              <Box
                component="img"
                src={imgSrc}
                alt="Header"
                sx={{
                  width: isCard ? '100%' : '25vw',
                  display: 'block',
                  height: '300px',
                  objectFit: 'contain',
                  cursor: onClick ? 'pointer' : 'default',
                }}
              />

              {showQuickShop && (
                <Box className={classes.quickShopOverlay}>
                  <Button
                    variant="contained"
                    color="secondary"
                    sx={{ borderRadius: 999, px: 4, py: 1.2 }}
                    onClick={event => {
                      event.stopPropagation();
                      onQuickShop();
                      if (event.currentTarget) {
                        event.currentTarget.blur();
                      }
                    }}>
                    <Typography
                      sx={{
                        color: 'text.primary',
                        fontSize: 14,
                        fontWeight: 700,
                        textTransform: 'uppercase',
                      }}>
                      {t('quickshop')}
                    </Typography>
                  </Button>
                </Box>
              )}

              {isCard && <Divider sx={{ mt: 2, mx: 2 }} />}
            </Box>

            <Box
              display="flex"
              flexDirection={'column'}
              justifyContent={'center'}
              sx={{ mt: 2, paddingLeft: 4, paddingRight: 4, cursor: onClick ? 'pointer' : 'default' }}>
              <Typography variant="h3" sx={{ mb: 2, textTransform: 'uppercase' }}>
                {title}
              </Typography>
              {identification && (
                <Typography fontSize={12} color="text.secondary" justifyContent={'center'} sx={{ mb: 1 }}>
                  {identification}
                </Typography>
              )}

              <Typography fontSize={12} justifyContent={'center'} sx={{ mb: 1 }}>
                <div
                  dangerouslySetInnerHTML={{
                    __html: formattedDescription,
                  }}
                />
              </Typography>
            </Box>
          </Box>

          <Box sx={{ pt: 4 }}>
            <Box
              flexDirection={isCard ? 'row' : 'column'}
              display="flex"
              justifyContent={isCard ? 'space-between' : 'flex-end'}
              sx={{ px: 4, height: '100%' }}>
              <Box onClick={onClick} sx={{ cursor: onClick ? 'pointer' : 'default', py: isCard ? 0 : 3 }}>
                {price > 0 && (
                  <Typography color="text.secondary">
                    {showStartAt && <Typography variant="overline">{t('startAt')}</Typography>}
                    {!showStartAt && <Box sx={{ pt: 3 }}></Box>}
                    <Typography
                      variant={'h4'}
                      color={promotionPrice ? 'text.secondary' : 'text.primary'}
                      sx={{
                        textDecorationLine: promotionPrice ? 'line-through' : 'none',
                        fontWeight: promotionPrice ? 'normal' : 'bold',
                      }}>
                      ${price.toFixed(2)} {currency}
                    </Typography>
                    {shouldShowQuantityPerPackageInfo(quantityPerPackage) && (
                      <Typography fontSize={12} color="text.secondary" sx={{ marginBottom: 0 }}>
                        ({t('quantityPerPackageInfo', { quantityPerPackage })})
                      </Typography>
                    )}
                  </Typography>
                )}

                {promotionPrice > 0 && (
                  <Box>
                    <Typography component="strong" variant="h3" color="error" fontWeight="bold">
                      ${promotionPrice.toFixed(2)} {currency}
                    </Typography>

                    {promotionQuantity != null && (
                      <Typography fontSize={12} color="text.secondary" justifyContent={'center'} sx={{ mb: -3, mt: 0 }}>
                        {t('promotionQuantityLeft', { count: promotionQuantity })}
                      </Typography>
                    )}

                    {inventoriedQuantityAvailable != null && (
                      <Typography fontSize={12} color="text.secondary" justifyContent={'center'} sx={{ mb: -3, mt: 0.5 }}>
                        {t('inventoriedQuantityLeft', { count: inventoriedQuantityAvailable })}
                      </Typography>
                    )}
                  </Box>
                )}

                {promotionPrice <= 0 && inventoriedQuantityAvailable != null && (
                  <Typography fontSize={12} color="text.secondary" justifyContent={'center'} sx={{ mb: -3, mt: 0.5 }}>
                    {t('inventoriedQuantityLeft', { count: inventoriedQuantityAvailable })}
                  </Typography>
                )}
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignContent: 'center',
                  alignItems: 'flex-end',
                  flexDirection: view === 'card' ? 'row' : 'column',
                  pointerEvents: 'auto', // clickable
                }}>
                {actions?.map(a =>
                  a.isIconButton ? (
                    <Fab key={a.id || uuidv4()} sx={{ m: 1 }} size="small" color={a.color} onClick={a.onClick}>
                      <a.icon />
                    </Fab>
                  ) : (
                    <Button
                      key={a.id || uuidv4()}
                      sx={{
                        borderRadius: 2,
                        maxHeight: 46,
                        backgroundColor: '#fff',
                        borderColor: '#000',
                        borderWidth: 1,
                        borderStyle: 'solid',
                        boxShadow: 'none',
                        '&:hover': {
                          backgroundColor: '#E8E9EB',
                          boxShadow: 'none',
                        },
                      }}
                      variant={a.variant || 'contained'}
                      color={a.color}
                      onClick={a.onClick}
                      disabled={a.disabled}>
                      <Typography
                        sx={{
                          color: 'black !important',
                          fontSize: 22,
                          fontWeight: 800,
                          paddingRight: 0,
                          paddingLeft: 0,
                          paddingTop: 2,
                          paddingBottom: 2,
                          fontFamily: 'Trade Gothic Next LT Pro',
                          letterSpacing: '-0.02em',
                          mb: 1,
                        }}>
                        {a.text}
                      </Typography>
                    </Button>
                  ),
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </CmtCardContent>
    </CmtCard>
  );
};

export default ItemListView;
